/**
 * Norway-only phone-number input.
 *
 * Renders a `+47` country-code chip flush against an 8-digit national
 * input, styled to read as a single field. Backed by Final Form via a
 * format/parse pair that keeps the canonical stored value in E.164
 * (`+47XXXXXXXX`) while showing only the spaced national digits in
 * the input. The user can't type or paste a different country code:
 * `parse` strips any leading `+47` (or bare `47`) and keeps the last
 * 8 digits, so pasting a full E.164 number works too.
 */
import React, { Component } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { ValidationError } from '../../components';

import { format, parse } from './e164Formatter';
import css from './FieldPhoneNumberInput.module.css';

const FieldPhoneNumberInputComponent = props => {
  const {
    rootClassName,
    className,
    labelClassName,
    customErrorText,
    id,
    label,
    input,
    meta,
    inputRef,
    hideErrorMessage,
    placeholder,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;
  const errorText = customErrorText || error;
  const hasError = !!customErrorText || !!(touched && invalid && error);
  const fieldMeta = { touched: hasError, error: errorText };

  const refMaybe = inputRef ? { ref: inputRef } : {};

  const groupClasses = classNames(css.inputGroup, {
    [css.inputGroupSuccess]: valid && !hasError,
    [css.inputGroupError]: hasError,
  });

  const rootClasses = classNames(rootClassName || css.root, className);
  const labelClassMaybe = labelClassName ? { className: labelClassName } : {};

  return (
    <div className={rootClasses}>
      {label ? (
        <label htmlFor={id} {...labelClassMaybe}>
          {label}
        </label>
      ) : null}
      <div className={groupClasses}>
        <span className={css.prefix} aria-hidden="true">
          +47
        </span>
        <input
          {...input}
          {...refMaybe}
          {...rest}
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder={placeholder}
          className={css.input}
        />
      </div>
      {hideErrorMessage ? null : <ValidationError fieldMeta={fieldMeta} />}
    </div>
  );
};

class FieldPhoneNumberInput extends Component {
  componentWillUnmount() {
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }
  render() {
    return (
      <Field
        component={FieldPhoneNumberInputComponent}
        format={format}
        parse={parse}
        {...this.props}
      />
    );
  }
}

export default FieldPhoneNumberInput;
